/**
 * Analytics tracking for TeleClaw
 * Fire-and-forget event tracking - doesn't block UI
 */

import { getInitDataRaw } from './telegram';

// API base URL - same as api.ts
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV
    ? 'http://localhost:8787'
    : window.location.hostname.includes('staging')
      ? 'https://teleclaw-api-staging.teleclaw.workers.dev'
      : 'https://teleclaw-api.teleclaw.workers.dev');

export type AnalyticsEvent = 'view' | 'click' | 'share';

export interface AnalyticsStats {
  views: number;
  clicks: number;
  shares: number;
}

/**
 * Track an analytics event for an agent
 * Fire-and-forget: doesn't await, doesn't block UI
 */
export function trackEvent(
  agentId: string,
  event: AnalyticsEvent,
  metadata?: Record<string, unknown>
): void {
  // Don't await - fire and forget
  const url = `${API_BASE_URL}/api/agents/${encodeURIComponent(agentId)}/track`;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add auth if available
  const initData = getInitDataRaw();
  if (initData) {
    headers['Authorization'] = `tma ${initData}`;
  }

  fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ event, metadata }),
  }).catch((err) => {
    // Silent fail - analytics shouldn't break the app
    console.debug('Analytics tracking failed:', err);
  });
}

/**
 * Get analytics stats for an agent
 */
export async function getStats(agentId: string): Promise<AnalyticsStats> {
  const url = `${API_BASE_URL}/api/agents/${encodeURIComponent(agentId)}/stats`;
  
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch stats');
  }

  return response.json();
}
